/**
 * The time pill: the site's time popover with a mouse, the device's own picker on touch
 * (STYLE_GUIDE "Date and time pickers", B-153). Times are 24-hour, like every time on the site.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, Clock, Sun, Sunrise, Sunset } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useMediaQuery, DESKTOP_POINTER } from '../../hooks/useMediaQuery';
import PickerPopover from './PickerPopover';
import { fromSeconds, midday, setPart, stepPart, toSeconds, TimePart } from './pickerMath';

interface TimeFieldProps {
  value?: string;
  onChange: (time: string) => void;
  sunrise?: string;
  sunset?: string;
  /** Where the time is read, e.g. "Ujjain, India": the clock is the place's, not the visitor's. */
  place?: string;
}

const PARTS: { part: TimePart; label: string; max: number }[] = [
  { part: 'h', label: 'Hours', max: 23 },
  { part: 'm', label: 'Minutes', max: 59 },
  { part: 's', label: 'Seconds', max: 59 },
];

/** Spins and typing reach the page after a short pause, so each step does not refetch the chart. */
export const COMMIT_DELAY_MS = 250;

const norm = (t?: string) => fromSeconds(toSeconds(t) ?? 0);

export const TimeField: React.FC<TimeFieldProps> = ({ value, onChange, sunrise, sunset, place }) => {
  const desktop = useMediaQuery(DESKTOP_POINTER);
  const [open, setOpen] = useState(false);
  const pill = useRef<HTMLButtonElement>(null);
  const [draft, setDraft] = useState(norm(value));
  const pending = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latest = useRef(draft);

  // Follow the page's time (Now, a new day's sunrise) unless an edit is still on its way out.
  useEffect(() => {
    if (!pending.current) {
      setDraft(norm(value));
      latest.current = norm(value);
    }
  }, [value]);

  const flush = useCallback(() => {
    if (pending.current) {
      clearTimeout(pending.current);
      pending.current = null;
      onChange(latest.current);
    }
  }, [onChange]);
  useEffect(() => flush, [flush]);

  const commit = (t: string, immediate = false) => {
    setDraft(t);
    latest.current = t;
    if (pending.current) clearTimeout(pending.current);
    pending.current = null;
    if (immediate) onChange(t);
    else pending.current = setTimeout(() => { pending.current = null; onChange(t); }, COMMIT_DELAY_MS);
  };

  const close = useCallback(() => { flush(); setOpen(false); }, [flush]);

  if (!desktop) {
    return (
      <div className="kg-pill flex h-[34px] items-center rounded-[9px] px-3 text-[13.5px] font-semibold">
        <input
          type="time"
          step="1"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          // Sized to its content: a fixed 100 px cut off "AM"/"PM" (B-153).
          className="w-auto min-w-[100px]"
          aria-label="Select time"
        />
      </div>
    );
  }

  const at = (t?: string) => (toSeconds(t) === null ? null : fromSeconds(toSeconds(t)!));
  const quick = [
    { label: 'Sunrise', icon: Sunrise, at: at(sunrise) },
    { label: 'Midday', icon: Sun, at: midday(sunrise, sunset) },
    { label: 'Sunset', icon: Sunset, at: at(sunset) },
  ].filter((q) => q.at);

  return (
    <>
      <button
        ref={pill}
        type="button"
        onClick={() => (open ? close() : setOpen(true))}
        onKeyDown={(e) => { if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); } }}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`Select time, ${draft}`}
        className="kg-pill flex h-[34px] items-center gap-2 rounded-[9px] px-3.5 text-[13.5px] font-semibold tabular-nums whitespace-nowrap"
        style={open ? { borderColor: 'var(--kg-accent)' } : undefined}
      >
        <Clock className="h-4 w-4" style={{ color: 'var(--kg-sub)' }} />
        <span style={{ color: 'var(--kg-ink)' }}>{draft}</span>
      </button>
      {open && (
        <PickerPopover anchor={pill} label="Choose time" onClose={close}>
          <TimePanel draft={draft} quick={quick} place={place} onCommit={commit} />
        </PickerPopover>
      )}
    </>
  );
};

interface Quick { label: string; icon: React.ComponentType<{ className?: string }>; at: string | null }

const TimePanel: React.FC<{ draft: string; quick: Quick[]; place?: string; onCommit: (t: string, immediate?: boolean) => void }> = ({ draft, quick, place, onCommit }) => {
  const spins = useRef<(HTMLDivElement | null)[]>([]);
  useEffect(() => { spins.current[0]?.focus(); }, []);

  return (
    <div
      className="w-[268px]"
      // Paste "18:30" or "18:30:15" anywhere in the panel.
      onPaste={(e) => {
        const t = toSeconds(e.clipboardData.getData('text'));
        if (t !== null) { e.preventDefault(); onCommit(fromSeconds(t), true); }
      }}
    >
      {quick.length > 0 && (
        <div className="mb-3 grid grid-cols-3 gap-1.5">
          {quick.map(({ label, icon: Icon, at }) => (
            <button
              key={label}
              type="button"
              aria-pressed={draft === at}
              onClick={() => onCommit(at!, true)}
              className={cn('kg-picker-cell kg-picker-chip flex flex-col items-center gap-0.5 rounded-[9px] py-2 text-[12px] font-medium', draft === at && 'kg-picker-chip-selected')}
            >
              <Icon className="h-4 w-4" />
              <span>{label}</span>
              <span className="tabular-nums text-[11.5px] opacity-80">{at!.slice(0, 5)}</span>
            </button>
          ))}
        </div>
      )}
      <div className="flex items-center justify-center gap-1">
        {PARTS.map(({ part, label, max }, i) => (
          <React.Fragment key={part}>
            {i > 0 && <span className="pb-0.5 text-[22px] font-semibold" style={{ color: 'var(--kg-faint)' }}>:</span>}
            <Spin
              ref={(el) => { spins.current[i] = el; }}
              time={draft}
              part={part}
              label={label}
              max={max}
              onCommit={onCommit}
              onAdvance={() => spins.current[i + 1]?.focus()}
            />
          </React.Fragment>
        ))}
      </div>
      <p className="mt-2 text-center text-[11.5px]" style={{ color: 'var(--kg-sub)' }}>
        {place ? `Time at ${place}` : 'Local time'} · 24-hour
      </p>
    </div>
  );
};

/**
 * One of hours, minutes, seconds: ▲ value ▼. Arrow keys wrap. Typing works like a native
 * time field: two digits fill the part and move on, as does a first digit no value can start
 * with (3-9 for hours, 6-9 for minutes and seconds); a lone digit is kept after a pause or on
 * leaving; Backspace clears what was typed.
 */
const Spin = React.forwardRef<HTMLDivElement, {
  time: string; part: TimePart; label: string; max: number;
  onCommit: (t: string, immediate?: boolean) => void; onAdvance: () => void;
}>(({ time, part, label, max, onCommit, onAdvance }, ref) => {
  const index = { h: 0, m: 1, s: 2 }[part];
  const value = Number(time.split(':')[index]);
  // Typed digits live in a ref as well as state: moving focus on after the second digit
  // blurs this field, and its blur handler must see the cleared buffer, not a stale render's.
  const [typed, setTypedState] = useState('');
  const typedRef = useRef('');
  const setTyped = (v: string) => { typedRef.current = v; setTypedState(v); };
  const idle = useRef<ReturnType<typeof setTimeout> | null>(null);
  const firstMax = Math.floor(max / 10);   // 2 for hours, 5 for minutes and seconds

  const finish = (digits: string, advance: boolean) => {
    if (idle.current) clearTimeout(idle.current);
    idle.current = null;
    setTyped('');
    if (digits) onCommit(setPart(time, part, Number(digits)));
    if (advance) onAdvance();
  };
  useEffect(() => () => { if (idle.current) clearTimeout(idle.current); }, []);

  const shown = typed ? typed.padStart(2, '0') : String(value).padStart(2, '0');
  const btn = 'kg-picker-icon flex h-7 w-12 items-center justify-center rounded-[8px]';
  return (
    <div className="flex flex-col items-center">
      <button type="button" tabIndex={-1} aria-label={`${label} up`} className={btn} onClick={() => onCommit(stepPart(time, part, 1))}>
        <ChevronUp className="h-4 w-4" />
      </button>
      <div
        ref={ref}
        role="spinbutton"
        tabIndex={0}
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-valuenow={typed ? Number(typed) : value}
        aria-valuetext={shown}
        onBlur={() => finish(typedRef.current, false)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            finish('', false);
            onCommit(stepPart(time, part, e.key === 'ArrowUp' ? 1 : -1));
          } else if (/^\d$/.test(e.key)) {
            e.preventDefault();
            const digits = typedRef.current + e.key;
            if (digits.length === 2 || (digits.length === 1 && Number(e.key) > firstMax)) {
              finish(digits, true);
            } else {
              setTyped(digits);
              if (idle.current) clearTimeout(idle.current);
              idle.current = setTimeout(() => finish(digits, false), 1000);
            }
          } else if (e.key === 'Backspace') {
            e.preventDefault();
            if (idle.current) clearTimeout(idle.current);
            setTyped('');
          } else if (e.key === ':' || e.key === 'Enter') {
            if (typedRef.current) { e.preventDefault(); finish(typedRef.current, e.key === ':'); }
            else if (e.key === ':') { e.preventDefault(); onAdvance(); }
          }
        }}
        className="kg-picker-spin flex h-11 w-12 items-center justify-center rounded-[9px] text-[22px] font-semibold tabular-nums"
        style={typed ? { borderColor: 'var(--kg-accent)' } : undefined}
      >
        {shown}
      </div>
      <button type="button" tabIndex={-1} aria-label={`${label} down`} className={btn} onClick={() => onCommit(stepPart(time, part, -1))}>
        <ChevronDown className="h-4 w-4" />
      </button>
    </div>
  );
});
Spin.displayName = 'Spin';

export default TimeField;
